import {
    ChangeDetectionStrategy,
    Component,
    model,
    OnChanges,
    OnDestroy,
    OnInit,
    Renderer2,
    signal,
    viewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { HttpStatusEnum } from '@core/api';
import { TranslateModule } from '@ngx-translate/core';
import { LoaderComponent, UnsavedChangesModalComponent } from '@shared/components';
import { ButtonModule } from 'primeng/button';
import { Sidebar, SidebarModule } from 'primeng/sidebar';
import { map } from 'rxjs';
import { DashboardApi } from '../../_core/api';
import { PickupLocation, ScheduleCourierPickupOrder } from '../../_core/interfaces';
import { ScheduleCourierPickupSubmissionModalData } from './core';
import { ScheduleCourierPickUpFormService } from './core/schedule-courier-pick-up-form.manager';
import { PickupSubmittedModalComponent } from './pickup-submitted-modal/pickup-submitted-modal.component';
import { ScheduleCourierPickUpFormComponent } from './schedule-courier-pick-up-form/schedule-courier-pick-up-form.component';

@Component({
    selector: 'app-schedule-courier-pick-up-drawer',
    standalone: true,
    imports: [
        TranslateModule,
        SidebarModule,
        LoaderComponent,
        ButtonModule,
        ScheduleCourierPickUpFormComponent,
        PickupSubmittedModalComponent,
        UnsavedChangesModalComponent,
    ],
    providers: [ScheduleCourierPickUpFormService],
    templateUrl: './schedule-courier-pick-up-drawer.component.html',
    styleUrls: ['./schedule-courier-pick-up-drawer.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ScheduleCourierPickUpDrawerComponent implements OnInit, OnChanges, OnDestroy {
    readonly drawer = viewChild<Sidebar>('drawer');

    readonly isDrawerVisible = model.required<boolean>();

    readonly hasModifiedInitialGeneralSettings = toSignal(
        this.formService
            .getScheduleCourierForm()
            .valueChanges.pipe(map(() => this.formService.hasModifiedInitialGeneralSettings())),
        { initialValue: false }
    );

    readonly isLoading = signal(false);
    readonly displayPickupSubmittedModal = signal(false);
    readonly displayProgressLostWarningModal = signal(false);

    readonly ordersList = signal<ScheduleCourierPickupOrder[]>([]);
    readonly pickupLocations = signal<PickupLocation[]>([]);
    readonly scheduleCourierSubmissionModalData = signal<ScheduleCourierPickupSubmissionModalData | null>(null);

    constructor(
        public formService: ScheduleCourierPickUpFormService,
        private dashboardApi: DashboardApi,
        private renderer: Renderer2
    ) {}

    ngOnInit(): void {
        this.scrollWhileDrawerIsOpen(true);
    }

    ngOnChanges() {
        this.getCourierDetails();
    }

    ngOnDestroy(): void {
        this.formService.hasModifiedInitialGeneralSettings();
        this.scrollWhileDrawerIsOpen(false);
    }

    private scrollWhileDrawerIsOpen(enableScroll: boolean) {
        const bodyElement = document.querySelector('body');

        if (enableScroll === true) {
            this.renderer.addClass(bodyElement, 'disable-overlay-scroll');
        } else {
            this.renderer.removeClass(bodyElement, 'disable-overlay-scroll');
        }
    }

    private async getCourierDetails() {
        this.isLoading.set(true);
        const response = await this.dashboardApi.getPrepareSchedulePickupData();
        if (response.status === HttpStatusEnum.SUCCESS && response.data) {
            this.formService.populateForm(response.data);
            this.pickupLocations.set(response.data.pickupLocations);
            this.ordersList.set(response.data.orders);
        }
        this.isLoading.set(false);
    }

    async saveAndClose() {
        const payload = this.formService.computePayload();
        this.scheduleCourierSubmissionModalData.set(this.computeScheduleCourierSubmissionModal());
        this.isLoading.set(true);

        const response = await this.dashboardApi.scheduleCourierPickup(payload);
        if (response.data && response.status === HttpStatusEnum.SUCCESS) this.displayPickupSubmittedModal.set(true);

        this.isLoading.set(false);
    }

    checkChangesToExit(event?: any) {
        if (event === true) {
            return;
        }

        if (this.hasModifiedInitialGeneralSettings()) {
            this.displayProgressLostWarningModal.set(true);
            return;
        }

        this.isDrawerVisible.set(false);
    }

    refreshScheduleCourierForm() {
        this.formService.resetForm();
        this.getCourierDetails();
        this.displayPickupSubmittedModal.set(false);
    }

    closeUnsavedChangesModal(): void {
        this.displayProgressLostWarningModal.set(false);
        this.drawer()?.show();
    }

    exitDrawer(): void {
        this.displayProgressLostWarningModal.set(false);
        this.isDrawerVisible.set(false);
    }

    computeScheduleCourierSubmissionModal(): ScheduleCourierPickupSubmissionModalData {
        return {
            name: `${this.formService.firstName.value} ${this.formService.lastName.value}`,
            location: this.formService.pickupLocation.value?.description ?? '',
            days: 'M-F',
            courierPickup: this.formService.pickupLocation.value?.pickupTime ?? '-',
            cutOffTime: this.formService.pickupLocation.value?.cutOffTime ?? '-',
            noOfTubes: this.formService.numberOfTubes?.value ?? 0,
        };
    }
}
